import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';

import { createGradientOrSolid, overrideCssVars } from '@appcharge/shared-ui';
import { EBlockType, TStoreBlock } from 'common/TemplateStudio.types';
import { zodResolver } from '@hookform/resolvers/zod';
import { CircularProgress } from '@mui/material';
import useFeatureFlags from 'api/useFeatureFlags';
import { useTemplatesStudio } from 'api/useTemplatesStudio';
import { useUserDetails } from 'api/useUserDetails';
import {
  BodyBlockId,
  BodyBlockV2Item,
  buildOnePlusManyInstanceId,
  DEFAULT_BODY_BLOCKS_ORDER,
  getBaseBlockType,
  isOnePlusManyInstanceId,
  MERGEABLE_BODY_BLOCKS
} from 'constants/bodyBlocks';
import { EEventsType, EFeatureFlag, StoreBodyBlock } from 'constants/enums';
import { useBigQueryEventsTracking } from 'hooks/useBigQueryTracking';
import { useWindowWidth } from 'hooks/useWindowWidth';
import { useTemplateStudioState } from 'state/hooks/template-studio.hook';
import {
  decodeRichTextContent,
  processRichTextContent
} from 'utils/colorUtils';
import { trackMixpanelEvent } from 'utils/mixpanelClient';
import { scrollToFirstErrorField } from 'utils/reactHookFormValidation.utils';
import { EToasterStatus, showToast } from 'utils/showToast';
import { showValidationToast } from 'utils/showValidationToast';
import { z } from 'zod';

import DialogModal from 'components/DialogModal/DialogModal';
import FormHeader from 'components/FormHeader/FormHeader';
import { cn } from 'components/lib/utils';
import { collapseOnePlusManyToSingleSlot } from 'components/TemplatesStudio/bodyBlocksOrderV2.utils';
import { buildDefaultOnePlusManyFormStoreBlock } from 'components/TemplatesStudio/multiInstanceBlocks';
import type { IFormStoreBlock } from 'components/TemplatesStudio/multiInstanceBlocks';
import {
  buildFormStoreBlocksFromBodyBlocksV2,
  buildStoreBlocksFromForm,
  deriveBodyBlocksOrderV2FromStoreBlocks,
  ensureBannerStoreBlock,
  hydrateFormStoreBlocksFromWire
} from 'components/TemplatesStudio/storeBlocks/storeBlocks.utils';
import {
  flattenBodyBlocksV2,
  getDefaultBodyBlocksOrderV2
} from 'components/TemplatesStudio/utils';
import { Form } from 'components/ui';
import { SidebarProvider } from 'components/ui/sidebar';

import {
  BRAND_KIT_WIDTH,
  TemplateStudioBrandKitContainer
} from './BrandKit/Container';
import { BrandKitController } from './Components/BrandKitController';
import { DeviceTypeController } from './Components/DeviceTypeController';
import { FormControllers } from './Components/FormControllers';
import { PersonalizationController } from './Components/PersonalizationController';
import { TitleInput } from './Components/TitleInput';
import { defaultTemplateStudioFormValues } from './defaults';
import { TemplateStudioPreview } from './Preview/Preview';
import { MAIN_SIDEBAR_WIDTH } from './Sidebar/Main/MainSidebar';
import {
  SECONDARY_SIDEBAR_WIDTH,
  TemplateStudioSidebar
} from './Sidebar/Sidebar';
import {
  PANEL_MIN_WIDTH,
  TestPersonalizationContainer
} from './TestPersonalization/Container';
import { computeFormLayout } from './utils/formLayout.utils';
import { buildValidationResult } from './utils/validation.utils';
import { templateStudioSchema } from './validation-schema';

const PANEL_RIGHT_GAP = 6;

const convertBodyBlocksOrderToV2 = (
  bodyBlocksOrder: BodyBlockId[]
): BodyBlockV2Item[] => {
  const allBlocks = Object.values(StoreBodyBlock);
  const visibleSet = new Set(bodyBlocksOrder);

  const toV2Item = (
    block: BodyBlockId,
    isVisible: boolean
  ): BodyBlockV2Item => ({
    id: `bb-${String(block)}`,
    blocks: [block],
    isVisible,
    isMergeable: isOnePlusManyInstanceId(block)
      ? false
      : MERGEABLE_BODY_BLOCKS.has(block as StoreBodyBlock)
  });

  const items: BodyBlockV2Item[] = bodyBlocksOrder.map((block) =>
    toV2Item(block, true)
  );

  for (const block of allBlocks) {
    if (!visibleSet.has(block)) {
      items.push(toV2Item(block, false));
    }
  }

  return items;
};

const HeaderSecondarySection: React.FC = () => {
  return (
    <div className="flex items-center gap-1">
      <PersonalizationController />
      <BrandKitController />
      <FormControllers />
      <DeviceTypeController />
    </div>
  );
};

export const TemplateStudioForm: React.FC<{
  edit?: boolean;
  dup?: boolean;
}> = ({ edit = false, dup = false }) => {
  const navigate = useNavigate();
  const { publisherId, pageId } = useParams();
  const { featureFlags } = useFeatureFlags({ publisherId });
  const { trackBigQueryEvent } = useBigQueryEventsTracking();
  const { userDetails } = useUserDetails();
  const { getTemplate, updateTemplate, getTemplates, createTemplate } =
    useTemplatesStudio(publisherId, pageId);
  const {
    defaultBodyBlocksOrder,
    resetSidebars,
    setScrollToSection,
    isPersonalizationOpen,
    isBrandKitOpen,
    setIsBrandKitOpen,
    isSecondarySidebarOpen,
    personalizationPanelWidth,
    setBrandKitTab,
    setOpenSidebarBlock
  } = useTemplateStudioState();
  const [formContainerWidth, setFormContainerWidth] = useState(0);
  const windowWidth = useWindowWidth();
  const [isUnsavedChangesModalOpen, setIsUnsavedChangesModalOpen] =
    useState(false);
  const [
    isSubmittingActiveTemplateModalOpen,
    setIsSubmittingActiveTemplateModalOpen
  ] = useState(false);
  const BUTTON_TEXT = edit ? 'Update' : 'Save';
  const formScrollContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = formScrollContainerRef.current;
    if (!el) return;
    const updateWidth = () => setFormContainerWidth(el.clientWidth);
    const ro = new ResizeObserver(updateWidth);
    ro.observe(el);
    updateWidth();
    return () => ro.disconnect();
  }, []);

  const sidebarWidth = isSecondarySidebarOpen
    ? MAIN_SIDEBAR_WIDTH + SECONDARY_SIDEBAR_WIDTH
    : MAIN_SIDEBAR_WIDTH;
  const panelWidth = personalizationPanelWidth ?? PANEL_MIN_WIDTH;
  const availableWidth = formContainerWidth || windowWidth;
  const { hasSpaceForInFlow } = computeFormLayout({
    sidebarWidth,
    brandKitWidth: isBrandKitOpen ? BRAND_KIT_WIDTH : 0,
    panelWidth,
    availableWidth
  });

  const formContentStyle: React.CSSProperties | undefined =
    isPersonalizationOpen
      ? { paddingRight: (hasSpaceForInFlow ? panelWidth : 0) + PANEL_RIGHT_GAP }
      : undefined;

  const initialValues = useMemo(() => {
    const bodyBlocksV2 = getDefaultBodyBlocksOrderV2(
      defaultBodyBlocksOrder || DEFAULT_BODY_BLOCKS_ORDER
    );
    return {
      ...defaultTemplateStudioFormValues,
      bodyBlocksOrder: defaultBodyBlocksOrder || DEFAULT_BODY_BLOCKS_ORDER,
      bodyBlocksOrderV2: bodyBlocksV2
    };
  }, [defaultBodyBlocksOrder]);

  const form = useForm<z.infer<typeof templateStudioSchema>>({
    resolver: zodResolver(templateStudioSchema),
    mode: 'onChange',
    defaultValues: initialValues
  });

  const {
    formState: { isDirty },
    watch,
    trigger
  } = form;

  const formValues = watch();

  useEffect(() => {
    const subscription = form.watch((values, { name }) => {
      if (name === 'storeBlocks') {
        const storeBlocks = values.storeBlocks as IFormStoreBlock[] | undefined;
        if (!storeBlocks) return;
        const v2 = deriveBodyBlocksOrderV2FromStoreBlocks(storeBlocks);
        form.setValue('bodyBlocksOrderV2', v2, {
          shouldValidate: true,
          shouldDirty: true
        });
        form.setValue('bodyBlocksOrder', flattenBodyBlocksV2(v2), {
          shouldValidate: true,
          shouldDirty: true
        });
        return;
      }
      if (name !== 'bodyBlocksOrderV2') return;
      const v2 = values.bodyBlocksOrderV2 as BodyBlockV2Item[] | undefined;
      if (!v2) return;
      form.setValue('bodyBlocksOrder', flattenBodyBlocksV2(v2), {
        shouldValidate: true,
        shouldDirty: true
      });
    });
    return () => subscription.unsubscribe();
  }, [form]);

  const initializeForm = useCallback(
    (rawData: Partial<z.infer<typeof templateStudioSchema>>) => {
      try {
        const data = rawData;
        const enabledBlocksSet = new Set<StoreBodyBlock>(
          initialValues.bodyBlocksOrder as StoreBodyBlock[]
        );

        const isAllowedBlock = (block: BodyBlockId): boolean =>
          enabledBlocksSet.has(getBaseBlockType(block));

        const v2Source = data.bodyBlocksOrderV2;
        const baseV2Source = collapseOnePlusManyToSingleSlot(
          v2Source?.length
            ? v2Source
            : convertBodyBlocksOrderToV2(
                data.bodyBlocksOrder ??
                  initialValues.bodyBlocksOrder ??
                  DEFAULT_BODY_BLOCKS_ORDER
              )
        );
        const filteredV2 = baseV2Source.filter((item) =>
          item.blocks.every(isAllowedBlock)
        );

        const presentBaseBlocks = new Set<StoreBodyBlock>(
          filteredV2.flatMap((item) => item.blocks.map(getBaseBlockType))
        );
        const missingBlocks = Array.from(enabledBlocksSet).filter(
          (block) => !presentBaseBlocks.has(block)
        );
        const seededFilteredV2 = filteredV2.map((item) => ({
          ...item,
          id: item.id ?? `bb-${item.blocks.map(String).join('+')}`
        }));
        const baseBodyBlocksOrderV2 = [
          ...seededFilteredV2,
          ...missingBlocks.map((block) => ({
            id: `bb-${String(block)}`,
            blocks: [block as BodyBlockId],
            isVisible: false,
            isMergeable: MERGEABLE_BODY_BLOCKS.has(block)
          }))
        ];

        const wireStoreBlocks = data.storeBlocks as unknown as
          | TStoreBlock[]
          | undefined;
        let storeBlocks = hydrateFormStoreBlocksFromWire(wireStoreBlocks);

        if (storeBlocks.length === 0) {
          storeBlocks = buildFormStoreBlocksFromBodyBlocksV2(
            baseBodyBlocksOrderV2,
            buildDefaultOnePlusManyFormStoreBlock
          );
        }

        if (
          !storeBlocks.some(
            (block) => block.type === EBlockType.ONE_PLUS_MANY
          ) &&
          baseBodyBlocksOrderV2.some((item) =>
            item.blocks.includes(StoreBodyBlock.onePlusMany)
          )
        ) {
          storeBlocks.push(
            buildDefaultOnePlusManyFormStoreBlock(buildOnePlusManyInstanceId(1))
          );
        }

        storeBlocks = ensureBannerStoreBlock(storeBlocks);

        const bodyBlocksOrderV2 =
          deriveBodyBlocksOrderV2FromStoreBlocks(storeBlocks);
        const bodyBlocksOrder = flattenBodyBlocksV2(bodyBlocksOrderV2);

        const formData = {
          ...initialValues,
          ...data,
          storeBlocks,
          bodyBlocksOrder,
          bodyBlocksOrderV2,
          richText: data.richText
            ? decodeRichTextContent(data.richText)
            : initialValues.richText
        };

        formData.dailyBonusBadge = {
          selectedDesignId: formData.dailyBonusBadge?.selectedDesignId || '',
          themes: formData.dailyBonusBadge?.themes || []
        };

        formData.triggeredPopups = {
          selectedDesignId:
            formData.triggeredPopups?.selectedDesignId ||
            initialValues.triggeredPopups!.selectedDesignId,
          selectedBadgeDesignId:
            formData.triggeredPopups?.selectedBadgeDesignId ||
            initialValues.triggeredPopups!.selectedBadgeDesignId,
          themes: formData.triggeredPopups?.themes || []
        };

        formData.rewardCalendar = {
          selectedDesignId:
            formData.rewardCalendar?.selectedDesignId ||
            initialValues.rewardCalendar!.selectedDesignId,
          selectedBadgeDesignId:
            formData.rewardCalendar?.selectedBadgeDesignId ||
            initialValues.rewardCalendar!.selectedBadgeDesignId,
          themes: formData.rewardCalendar?.themes || []
        };

        form.reset(formData);
      } catch (error) {
        console.error('Error initializing form:', error);
      }
    },
    [form, initialValues]
  );

  useEffect(() => {
    if (!edit && !dup) {
      initializeForm(initialValues);
    }
    if (edit && getTemplate.data) {
      initializeForm(
        getTemplate.data as unknown as Partial<z.infer<typeof templateStudioSchema>>
      );
    } else if (dup && getTemplate.data) {
      initializeForm({
        ...getTemplate.data,
        templateName: `${getTemplate.data.templateName}_duplicated`
      } as unknown as Partial<z.infer<typeof templateStudioSchema>>);
    }

    if ((edit || dup) && getTemplate.isError) {
      showToast({
        message: 'Could not load template',
        status: EToasterStatus.ERROR
      });
      navigate('../');
    }
  }, [
    edit,
    dup,
    getTemplate.data,
    getTemplate.isError,
    getTemplate.error,
    initialValues
  ]);

  useEffect(() => {
    setScrollToSection('header');
    return () => {
      resetSidebars();
      setScrollToSection(undefined);
    };
  }, []);

  const isLoading = useMemo(() => {
    return (
      (edit && getTemplate.isLoading) ||
      createTemplate.isLoading ||
      updateTemplate.isLoading ||
      defaultBodyBlocksOrder === undefined
    );
  }, [
    edit,
    getTemplate.isLoading,
    createTemplate.isLoading,
    updateTemplate.isLoading,
    defaultBodyBlocksOrder
  ]);

  const isSaveEnabled = useMemo(
    () => !createTemplate.isLoading && !updateTemplate.isLoading,
    [createTemplate.isLoading, updateTemplate.isLoading]
  );

  const handleLeave = () => {
    setIsUnsavedChangesModalOpen(false);
    navigate('../');
  };

  const handleCloseFormClick = () => {
    if (isDirty) {
      setIsUnsavedChangesModalOpen(true);
    } else {
      handleLeave();
    }
  };

  const onSubmit = async (data: z.infer<typeof templateStudioSchema>) => {
    try {
      const shouldSendStoreBlocks =
        !!featureFlags?.[
          EFeatureFlag.DASHBOARD_SEND_STOREBLOCKS_TEMPLATE_STUDIO
        ];
      const { storeBlocks: _formStoreBlocks, ...dataWithoutStoreBlocks } = data;
      const apiData = {
        ...dataWithoutStoreBlocks,
        ...(shouldSendStoreBlocks
          ? { storeBlocks: buildStoreBlocksFromForm(data) }
          : {}),
        richText: processRichTextContent(data.richText),
        externalButtons: {
          ...data.externalButtons,
          addToHomeScreenButton: {
            ...data.externalButtons.addToHomeScreenButton,
            iconImage:
              data.externalButtons.addToHomeScreenButton.iconImage ?? '',
            shortcutName:
              data.externalButtons.addToHomeScreenButton.shortcutName ?? ''
          },
          backToGameButton: {
            ...data.externalButtons.backToGameButton,
            buttonImage: data.externalButtons.backToGameButton.buttonImage ?? ''
          }
        }
      };

      if (edit && pageId && !dup) {
        const { templateName } = data;
        await updateTemplate.mutateAsync(
          { form: apiData, templateId: pageId },
          {
            onSuccess: () => {
              showToast({
                message: `${templateName} was updated successfully`,
                status: EToasterStatus.SUCCESS
              });
              getTemplates.refetch();
              handleLeave();
            },
            onError: () => {
              showToast({
                message: `Error updating ${templateName}`,
                status: EToasterStatus.ERROR
              });
            }
          }
        );
      } else {
        await createTemplate.mutateAsync(apiData, {
          onSuccess: () => {
            showToast({
              message: 'Template was created successfully',
              status: EToasterStatus.SUCCESS
            });
            getTemplates.refetch();
            handleLeave();
          },
          onError: (error: any) => {
            showToast({
              message: 'Error creating template',
              description:
                error?.response?.data?.message || 'Unknown error occurred',
              status: EToasterStatus.ERROR
            });
          }
        });
      }
    } catch (error) {
      console.error('Form submission error:', error);
    }
  };

  const navigateToFirstError = useCallback(
    (validationErrors: typeof form.formState.errors) => {
      const { firstFieldLocation, toastMessage, toastDescriptions } =
        buildValidationResult(validationErrors as Record<string, unknown>);

      showValidationToast({
        message: toastMessage,
        descriptions: toastDescriptions
      });

      if (!firstFieldLocation) return;

      if (firstFieldLocation.type === 'brandKit') {
        setIsBrandKitOpen(true);
        setBrandKitTab(firstFieldLocation.tab);
      } else if (firstFieldLocation.type === 'sidebar') {
        setOpenSidebarBlock(firstFieldLocation.block);
        setScrollToSection(firstFieldLocation.block);
      }

      scrollToFirstErrorField(firstFieldLocation.scrollTarget);
    },
    [setIsBrandKitOpen, setBrandKitTab, setOpenSidebarBlock, setScrollToSection]
  );

  const handleFormSubmit = useCallback(async () => {
    const isValid = await trigger();

    if (!isValid) {
      navigateToFirstError(form.formState.errors);
      return;
    }

    if (edit && !isDirty && !dup) {
      handleLeave();
      return;
    }

    if (getTemplate.data?.isActive) {
      setIsSubmittingActiveTemplateModalOpen(true);
    } else {
      form.handleSubmit(onSubmit)();
    }

    trackMixpanelEvent(EEventsType.DASHBOARD_TEMPLATE_ACTION, {
      publisher_id: publisherId,
      user_id: userDetails?.userId,
      company_id: userDetails?.company,
      templateId: getTemplate.data?.templateId,
      templateAction: 'save',
      isActiveTemplate: getTemplate.data?.isActive,
      templateName: getTemplate.data?.templateName
    });

    trackBigQueryEvent({
      eventName: EEventsType.DASHBOARD_TEMPLATE_ACTION,
      publisherId: publisherId,
      userId: userDetails?.userId,
      template_id: getTemplate.data?.templateId,
      action: 'save',
      is_active: getTemplate.data?.isActive,
      template_name: getTemplate.data?.templateName
    });
  }, [
    trigger,
    form,
    navigateToFirstError,
    edit,
    isDirty,
    dup,
    getTemplate.data,
    onSubmit,
    publisherId,
    userDetails,
    trackBigQueryEvent
  ]);

  const handleConfirmActiveTemplateSubmit = () => {
    setIsSubmittingActiveTemplateModalOpen(false);
    form.handleSubmit(onSubmit)();
  };

  useEffect(() => {
    const cssMappingOverride = {
      '--button-text-color': formValues.brandKit.buttonTextColor,
      '--button-bg-color': createGradientOrSolid(
        formValues.brandKit.buttonColor
      ),
      '--font-family': formValues.brandKit.fontFamily
    };

    overrideCssVars(cssMappingOverride, document);
  }, [
    formValues.brandKit.buttonColor,
    formValues.brandKit.buttonTextColor,
    formValues.brandKit.fontFamily
  ]);

  useEffect(() => {
    return () => {
      const body = document.body;
      if (body.style.pointerEvents === 'none') {
        body.style.pointerEvents = '';
      }
    };
  }, [isUnsavedChangesModalOpen, isSubmittingActiveTemplateModalOpen]);

  return (
    <div className="template-studio-form-container bg-neutral-100 fixed h-[100vh] w-full top-0 left-0 z-[1000] p-1">
      <SidebarProvider defaultOpen={true} isKeyboardShortcutDisabled>
        <div
          ref={formScrollContainerRef}
          className="bg-white h-full w-full flex overflow-scroll rounded-xl shadow-lg border border-neutral-200"
        >
          {isLoading && (
            <div className="absolute w-full h-full top-0 left-0 z-50 flex items-center justify-center bg-white/60 backdrop-blur-sm">
              <CircularProgress />
            </div>
          )}
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="template-studio-form flex flex-col w-full mx-auto h-[100vh]"
            >
              <FormHeader
                TitleComponent={TitleInput}
                SecondarySection={HeaderSecondarySection}
                mainButtonText={BUTTON_TEXT}
                onCloseForm={handleCloseFormClick}
                onMainButtonClick={handleFormSubmit}
                closeFormOnSubmit={false}
                closeIcon="fa-arrow-left-to-bracket"
                isFormValid={isSaveEnabled}
                className="py-3 select-none"
                dataTestId="TemplatesStudio-FormHeader"
                mainButtonDataTestId="TemplatesStudio-FormSave"
              />
              {/* {!isValid && Object.keys(errors).length > 0 && (
              <div className="mx-4 mt-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                <p className="font-medium">Please fix the following:</p>
                <ul className="mt-1 list-inside list-disc">
                  {getFlattenedErrorMessages(
                    errors as Record<string, unknown>
                  ).map((msg, i) => (
                    <li key={i}>{msg}</li>
                  ))}
                </ul>
              </div>
            )} */}
              <div
                className={cn(
                  'form-content flex flex-grow gap-4 min-w-0 w-full overflow-y-hidden pt-6 pl-4',
                  !isPersonalizationOpen && 'pr-4',
                  isPersonalizationOpen &&
                    !hasSpaceForInFlow &&
                    'overflow-x-auto'
                )}
                style={formContentStyle}
              >
                <TemplateStudioSidebar />
                <TemplateStudioPreview isLoadingForm={isLoading} />
                <TemplateStudioBrandKitContainer />
                <TestPersonalizationContainer
                  hasSpaceForInFlow={hasSpaceForInFlow}
                />
              </div>
            </form>
          </Form>
        </div>

        {isSubmittingActiveTemplateModalOpen && (
          <DialogModal
            isOpen={isSubmittingActiveTemplateModalOpen}
            title="Are you sure?"
            description="When you change an active template, the store will update to reflect its new values."
            data-testid="TemplatesStudio-ActiveTemplateSubmitModal"
            modalActions={{
              onConfirm: {
                label: 'Save',
                action: handleConfirmActiveTemplateSubmit,
                testId: 'TemplatesStudio-ActiveTemplateSubmitModal-save'
              },
              onClose: {
                label: 'Cancel',
                action: () => setIsSubmittingActiveTemplateModalOpen(false),
                variant: 'outline',
                testId: 'TemplatesStudio-ActiveTemplateSubmitModal-cancel'
              }
            }}
          />
        )}

        {isUnsavedChangesModalOpen && (
          <DialogModal
            isOpen={isUnsavedChangesModalOpen}
            title={'Unsaved Changes'}
            description={
              'You’ve made changes to the template that haven’t been saved. Leaving now will discard your changes.'
            }
            data-testid="TemplatesStudio-UnsavedChangesModal"
            modalActions={{
              onConfirm: {
                label: 'Continue',
                action: () => {
                  setIsUnsavedChangesModalOpen(false);
                },
                testId: 'TemplatesStudio-UnsavedChangesModal-continue'
              },
              onClose: {
                label: 'Leave',
                action: handleLeave,
                variant: 'outline',
                testId: 'TemplatesStudio-UnsavedChangesModal-leave'
              }
            }}
          />
        )}
      </SidebarProvider>
    </div>
  );
};
